import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "@studio-freight/lenis";

// Components
import { Toaster } from "./components/ui/sonner";
import { AnalyticsProvider, trackPageView } from "./components/Analytics";
import StickyCTA from "./components/StickyCTA";

// Pages - Lazy loaded for code splitting
const HomePage = lazy(() => import("./pages/HomePage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage"));
const HarvestPage = lazy(() => import("./pages/HarvestPage"));
const NaturePage = lazy(() => import("./pages/NaturePage"));
const BlogDetailPage = lazy(() => import("./pages/BlogDetailPage"));
const PartnerPage = lazy(() => import("./pages/PartnerPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));

// Loading Component
const PageLoader = () => (
  <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-2 border-[#4A5D23] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="font-body text-sm text-[#1A1A1A]/60">Loading...</p>
    </div>
  </div>
);

// Analytics route tracker
const RouteTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  
  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: "vertical",
      gestureDirection: "vertical",
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  return (
    <HelmetProvider>
      <AnalyticsProvider>
        <div className="App">
          <div className="grain-overlay" aria-hidden="true" />
          <BrowserRouter>
            <RouteTracker />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/products/:productId" element={<ProductDetailPage />} />
                <Route path="/harvest" element={<HarvestPage />} />
                <Route path="/nature" element={<NaturePage />} />
                <Route path="/nature/:slug" element={<BlogDetailPage />} />
                <Route path="/partner" element={<PartnerPage />} />
                <Route path="/about" element={<AboutPage />} />
              </Routes>
            </Suspense>
            <StickyCTA />
          </BrowserRouter>
          <Toaster position="bottom-right" />
        </div>
      </AnalyticsProvider>
    </HelmetProvider>
  );
}

export default App;
